import React, { useEffect, useState, useCallback } from "react";
import { BuyerTermsFields } from "@/components/accounts/BuyerTermsFields";
import { useAgents } from "@/lib/useAgents";
import { EMPTY_BUYER_TERMS, buyerTermsFrom, buyerTermsPayload, type BuyerTermsForm } from "@/lib/buyerTerms";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Building2,
  Users,
  Briefcase,
  FileText,
  Mail,
  Clock,
  CheckSquare2,
  Globe,
  Phone,
  MapPin,
  TrendingUp,
  Receipt,
  Plus,
  Edit,
  FileSignature,
} from "lucide-react";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import { WhatsAppChatDialog } from "@/components/whatsapp/WhatsAppChatDialog";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { EntityDetailLayout, type TabItem } from "@/components/entity-detail/EntityDetailLayout";
import { EntityHeader } from "@/components/entity-detail/EntityHeader";
import { EntityInfoSidebar, type InfoSection } from "@/components/entity-detail/EntityInfoSidebar";
import { DealsWidget } from "@/components/entity-detail/DealsWidget";
import { ContactsWidget } from "@/components/entity-detail/ContactsWidget";
import { QuotesWidget } from "@/components/entity-detail/QuotesWidget";
import { EmailsWidget } from "@/components/entity-detail/EmailsWidget";
import { WhatsAppWidget } from "@/components/entity-detail/WhatsAppWidget";
import { TasksWidget } from "@/components/entity-detail/TasksWidget";
import { ActivityTimeline } from "@/components/entity-detail/ActivityTimeline";
import { INDIAN_STATES, stateFromGstin } from "@/lib/indianStates";
import { formatINR } from "@/lib/utils";
import type { Account360, ContactRecord } from "@/types";
import type { WhatsAppThread } from "@/types/whatsapp";

export function AccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success, error: toastError } = useToast();
  const { hasPermission } = useAuth();

  const [data, setData] = useState<Account360 | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Dialogs
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    industry: "",
    website: "",
    email: "",
    phone: "",
    billing_address: "",
    shipping_address: "",
    state: "",
    gstin: "",
    notes: "",
  });
  const [terms, setTerms] = useState<BuyerTermsForm>(EMPTY_BUYER_TERMS);
  const agents = useAgents();
  const [editLoading, setEditLoading] = useState(false);

  // WhatsApp Chat Dialog
  const [waDialogOpen, setWaDialogOpen] = useState(false);
  const [waPhone, setWaPhone] = useState("");
  const [waName, setWaName] = useState("");

  // Quick Task Dialog
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);

  // Add Contact Dialog
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    mobile: "",
    job_title: "",
    is_primary: false,
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);

  const fetch360 = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await api.get(`/api/Account/360/${id}`);
      const payload = unwrapObject<Account360>(res);
      setData(payload);
      if (payload?.account) {
        setEditForm({
          name: payload.account.name || "",
          industry: payload.account.industry || "",
          website: payload.account.website || "",
          email: payload.account.email || "",
          phone: payload.account.phone || "",
          billing_address: payload.account.billing_address || "",
          shipping_address: payload.account.shipping_address || "",
          state: payload.account.state || "",
          gstin: payload.account.gstin || "",
          notes: payload.account.notes || "",
        });
        setTerms(buyerTermsFrom(payload.account));
      }
    } catch (err: any) {
      toastError("Failed to load account", err.message || "Account not found");
      navigate("/accounts");
    } finally {
      setLoading(false);
    }
  }, [id, navigate, toastError]);

  useEffect(() => {
    fetch360();
  }, [fetch360]);

  if (loading || !data) {
    return (
      <div className="p-6 space-y-6">
        <FormSkeleton />
      </div>
    );
  }

  const { account, contacts, deals, quotations, emails, whatsapp, tasks } = data;
  const history = data.history || [];

  const totalDealValue = deals.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
  const openTasksCount = tasks.filter((t) => t.task_status === "OPEN").length;

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      setEditLoading(true);
      await api.put(`/api/Account/Update/${id}`, { ...editForm, ...buyerTermsPayload(terms) });
      success("Account updated", "Account details have been successfully saved.");
      setEditOpen(false);
      fetch360();
    } catch (err: any) {
      toastError("Update failed", err.message || "Could not update account.");
    } finally {
      setEditLoading(false);
    }
  };

  const handleAddContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      setContactSubmitting(true);
      await api.post("/api/Contact/Insert", {
        ...contactForm,
        account_id: id,
      });
      success("Contact added", "New contact has been linked to this account.");
      setAddContactOpen(false);
      setContactForm({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        mobile: "",
        job_title: "",
        is_primary: false,
      });
      fetch360();
    } catch (err: any) {
      toastError("Failed to add contact", err.message || "Could not create contact.");
    } finally {
      setContactSubmitting(false);
    }
  };

  const openWhatsApp = (phone: string, name?: string) => {
    setWaPhone(phone);
    setWaName(name || account.name);
    setWaDialogOpen(true);
  };

  // Sidebar info configuration
  const sidebarSections: InfoSection[] = [
    {
      title: "Contact Details",
      fields: [
        {
          icon: Phone,
          label: "Phone",
          value: account.phone,
          href: account.phone ? `tel:${account.phone}` : undefined,
          copyable: true,
        },
        {
          icon: Mail,
          label: "Corporate Email",
          value: account.email,
          href: account.email ? `mailto:${account.email}` : undefined,
          copyable: true,
        },
        {
          icon: Globe,
          label: "Website",
          value: account.website,
          href: account.website ? (account.website.startsWith("http") ? account.website : `https://${account.website}`) : undefined,
          isExternal: true,
        },
        {
          icon: Building2,
          label: "Buyer Type",
          value: account.industry,
        },
      ],
    },
    {
      title: "Tax & Regional",
      fields: [
        {
          icon: Receipt,
          label: "GSTIN",
          value: account.gstin,
          copyable: true,
        },
        {
          icon: MapPin,
          label: "State",
          value: account.state,
        },
      ],
    },
    {
      title: "Trade & Credit",
      fields: [
        { icon: MapPin, label: "Market / City", value: account.city },
        {
          icon: Receipt,
          label: "Credit",
          value:
            account.credit_limit || account.credit_days
              ? [account.credit_limit ? formatINR(account.credit_limit) : null, account.credit_days ? `${account.credit_days} days` : null].filter(Boolean).join(" · ")
              : undefined,
        },
        { icon: Receipt, label: "Payment Terms", value: account.payment_terms },
        { icon: Building2, label: "Transporter", value: account.transporter },
        {
          icon: Building2,
          label: "Agent / Broker",
          value: account.agent_id ? agents.find((a) => a.id === account.agent_id)?.name || "Agent (inactive)" : "Direct",
        },
      ],
    },
    {
      title: "Addresses",
      fields: [
        {
          icon: MapPin,
          label: "Billing Address",
          value: account.billing_address,
        },
        {
          icon: MapPin,
          label: "Shipping Address",
          value: account.shipping_address,
        },
      ],
    },
  ];

  // Overview Tab Component
  const OverviewTab = (
    <div className="space-y-6">
      {/* Metric Cards Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div
          onClick={() => setActiveTab("deals")}
          className="bg-card border border-border/80 rounded-2xl p-4 shadow-2xs hover:border-primary/50 transition-colors cursor-pointer space-y-1"
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Deals</span>
            <div className="size-7 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center">
              <Briefcase className="size-3.5" />
            </div>
          </div>
          <div className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">{deals.length}</div>
          <p className="text-[11px] font-mono text-muted-foreground font-semibold">{formatINR(totalDealValue)}</p>
        </div>

        <div
          onClick={() => setActiveTab("contacts")}
          className="bg-card border border-border/80 rounded-2xl p-4 shadow-2xs hover:border-primary/50 transition-colors cursor-pointer space-y-1"
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Contacts</span>
            <div className="size-7 rounded-lg bg-violet-500/10 text-violet-600 dark:text-violet-400 flex items-center justify-center">
              <Users className="size-3.5" />
            </div>
          </div>
          <div className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">{contacts.length}</div>
          <p className="text-[11px] text-muted-foreground">Representatives</p>
        </div>

        <div
          onClick={() => setActiveTab("quotes")}
          className="bg-card border border-border/80 rounded-2xl p-4 shadow-2xs hover:border-primary/50 transition-colors cursor-pointer space-y-1"
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Quotes</span>
            <div className="size-7 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center">
              <FileText className="size-3.5" />
            </div>
          </div>
          <div className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">{quotations.length}</div>
          <p className="text-[11px] text-muted-foreground">Issued quotations</p>
        </div>

        <div
          onClick={() => setActiveTab("tasks")}
          className="bg-card border border-border/80 rounded-2xl p-4 shadow-2xs hover:border-primary/50 transition-colors cursor-pointer space-y-1"
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Pending Tasks</span>
            <div className="size-7 rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
              <CheckSquare2 className="size-3.5" />
            </div>
          </div>
          <div className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">{openTasksCount}</div>
          <p className="text-[11px] text-muted-foreground">{tasks.length} total tasks</p>
        </div>
      </div>

      {/* Recent Activity Timeline Preview */}
      <ActivityTimeline
        deals={deals}
        quotations={quotations}
        emails={emails}
        whatsapp={whatsapp}
        tasks={tasks}
        history={history}
        onOpenWhatsApp={(t) => openWhatsApp(t.conversation.wa_id, t.conversation.profile_name || undefined)}
      />
    </div>
  );

  const tabs: TabItem[] = [
    { id: "overview", label: "Overview", icon: TrendingUp, content: OverviewTab },
    {
      id: "contacts",
      label: "Contacts",
      icon: Users,
      count: contacts.length,
      content: (
        <ContactsWidget
          contacts={contacts}
          onAddContact={() => setAddContactOpen(true)}
          onWhatsAppContact={(c) => openWhatsApp(c.mobile || c.phone || "", `${c.first_name} ${c.last_name || ""}`)}
          canCreate={hasPermission("contact.create")}
        />
      ),
    },
    {
      id: "deals",
      label: "Deals",
      icon: Briefcase,
      count: deals.length,
      content: (
        <DealsWidget
          deals={deals}
          onNewDeal={() => navigate(`/deals?create=true&account_id=${account.id}&account_name=${encodeURIComponent(account.name)}`)}
          canCreate={hasPermission("deal.create")}
        />
      ),
    },
    {
      id: "quotes",
      label: "Quotes",
      icon: FileText,
      count: quotations.length,
      content: (
        <QuotesWidget
          quotes={quotations}
          canCreate={hasPermission("quote.create")}
        />
      ),
    },
    {
      id: "emails",
      label: "Emails",
      icon: Mail,
      count: emails.length,
      content: (
        <EmailsWidget
          emails={emails}
          canSend={hasPermission("email.send")}
        />
      ),
    },
    {
      id: "whatsapp",
      label: "WhatsApp",
      icon: WhatsAppIcon as any,
      count: whatsapp.length,
      content: (
        <WhatsAppWidget
          threads={whatsapp}
          onOpenChat={(t) => openWhatsApp(t.conversation.wa_id, t.conversation.profile_name || undefined)}
          onStartChat={account.phone ? () => openWhatsApp(account.phone!, account.name) : undefined}
          canSend={hasPermission("whatsapp.send")}
        />
      ),
    },
    {
      id: "tasks",
      label: "Tasks",
      icon: CheckSquare2,
      count: openTasksCount,
      content: (
        <TasksWidget
          tasks={tasks}
          relatedType="ACCOUNT"
          relatedId={account.id}
          relatedName={account.name}
          onRefresh={fetch360}
          canCreate={hasPermission("activity.create")}
        />
      ),
    },
    {
      id: "timeline",
      label: "Timeline",
      icon: Clock,
      content: (
        <ActivityTimeline
          deals={deals}
          quotations={quotations}
          emails={emails}
          whatsapp={whatsapp}
          tasks={tasks}
          history={history}
          onOpenWhatsApp={(t) => openWhatsApp(t.conversation.wa_id, t.conversation.profile_name || undefined)}
        />
      ),
    },
  ];

  return (
    <>
      <EntityDetailLayout
        breadcrumbs={[
          { label: "Accounts", href: "/accounts" },
          { label: account.name },
        ]}
        header={
          <EntityHeader
            type="ACCOUNT"
            title={account.name}
            subtitle={account.industry ? `Industry: ${account.industry}` : undefined}
            icon={Building2}
            avatarBg="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20"
            owner={account.owner ? { id: account.owner.id, name: `${account.owner.first_name} ${account.owner.last_name || ""}`.trim(), email: account.owner.email } : null}
            createdOn={account.created_on}
            onEdit={hasPermission("account.update") ? () => setEditOpen(true) : undefined}
            onWhatsApp={account.phone ? () => openWhatsApp(account.phone!, account.name) : undefined}
            onEmail={account.email ? () => window.open(`mailto:${account.email}`) : undefined}
            onAddTask={() => setTaskDialogOpen(true)}
            hasWhatsApp={Boolean(account.phone && hasPermission("whatsapp.send"))}
            hasEmail={Boolean(account.email)}
            backUrl="/accounts"
          />
        }
        sidebar={
          <EntityInfoSidebar
            sections={sidebarSections}
            notes={account.notes}
          />
        }
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Edit Account Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <form onSubmit={handleEditSubmit}>
            <DialogHeader>
              <DialogTitle>Edit Account</DialogTitle>
              <DialogDescription>
                Update company profile, industry, contacts, and tax information.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 sm:space-y-4.5 py-4">
              <div className="space-y-2">
                <Label htmlFor="acc_name">Company / Organization Name *</Label>
                <Input
                  id="acc_name"
                  value={editForm.name}
                  onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="acc_industry">Buyer Type</Label>
                  <Input
                    id="acc_industry"
                    value={editForm.industry}
                    onChange={(e) => setEditForm((p) => ({ ...p, industry: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="acc_website">Website</Label>
                  <Input
                    id="acc_website"
                    placeholder="company.com"
                    value={editForm.website}
                    onChange={(e) => setEditForm((p) => ({ ...p, website: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="acc_email">Corporate Email</Label>
                  <Input
                    id="acc_email"
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="acc_phone">Main Phone</Label>
                  <Input
                    id="acc_phone"
                    value={editForm.phone}
                    onChange={(e) => setEditForm((p) => ({ ...p, phone: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="acc_gstin">GSTIN</Label>
                  <Input
                    id="acc_gstin"
                    placeholder="27AABCU9603R1ZX"
                    value={editForm.gstin}
                    onChange={(e) => {
                      const v = e.target.value.toUpperCase();
                      setEditForm((p) => ({ ...p, gstin: v, state: stateFromGstin(v) || p.state }));
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="acc_state">State</Label>
                  <select
                    id="acc_state"
                    className="w-full h-10 sm:h-11 rounded-lg border border-input bg-background px-3 text-sm sm:text-[15px] focus:outline-hidden focus:ring-2 focus:ring-ring"
                    value={editForm.state}
                    onChange={(e) => setEditForm((p) => ({ ...p, state: e.target.value }))}
                  >
                    <option value="">-- Select State --</option>
                    {INDIAN_STATES.map((st) => (
                      <option key={st.code} value={st.name}>
                        {st.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="acc_billing">Billing Address</Label>
                <Input
                  id="acc_billing"
                  value={editForm.billing_address}
                  onChange={(e) => setEditForm((p) => ({ ...p, billing_address: e.target.value }))}
                />
              </div>

              <BuyerTermsFields value={terms} onChange={setTerms} idPrefix="acc" />

              <div className="space-y-2">
                <Label htmlFor="acc_notes">Notes</Label>
                <textarea
                  id="acc_notes"
                  rows={3}
                  className="w-full rounded-lg border border-input bg-background p-3 text-sm sm:text-[15px] focus:outline-hidden focus:ring-2 focus:ring-ring"
                  value={editForm.notes}
                  onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={editLoading}>
                {editLoading ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Contact Dialog */}
      <Dialog open={addContactOpen} onOpenChange={setAddContactOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <form onSubmit={handleAddContactSubmit}>
            <DialogHeader>
              <DialogTitle>Add Contact to {account.name}</DialogTitle>
              <DialogDescription>
                Add a new stakeholder or representative for this account.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 sm:space-y-4.5 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cnt_first">First Name *</Label>
                  <Input
                    id="cnt_first"
                    value={contactForm.first_name}
                    onChange={(e) => setContactForm((p) => ({ ...p, first_name: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cnt_last">Last Name</Label>
                  <Input
                    id="cnt_last"
                    value={contactForm.last_name}
                    onChange={(e) => setContactForm((p) => ({ ...p, last_name: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cnt_title">Job Title / Designation</Label>
                <Input
                  id="cnt_title"
                  placeholder="e.g. Procurement Manager"
                  value={contactForm.job_title}
                  onChange={(e) => setContactForm((p) => ({ ...p, job_title: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cnt_email">Email</Label>
                  <Input
                    id="cnt_email"
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm((p) => ({ ...p, email: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cnt_phone">Mobile / WhatsApp</Label>
                  <Input
                    id="cnt_phone"
                    value={contactForm.mobile}
                    onChange={(e) => setContactForm((p) => ({ ...p, mobile: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2.5 pt-2">
                <input
                  type="checkbox"
                  id="cnt_primary"
                  checked={contactForm.is_primary}
                  onChange={(e) => setContactForm((p) => ({ ...p, is_primary: e.target.checked }))}
                  className="rounded border-input text-primary focus:ring-primary size-4"
                />
                <Label htmlFor="cnt_primary" className="cursor-pointer font-medium">
                  Set as Primary Contact for this Account
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddContactOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={contactSubmitting}>
                {contactSubmitting ? "Adding..." : "Add Contact"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Chat Dialog */}
      {waPhone && (
        <WhatsAppChatDialog
          open={waDialogOpen}
          onOpenChange={(v) => {
            setWaDialogOpen(v);
            if (!v) fetch360();
          }}
          phone={waPhone}
          name={waName}
        />
      )}

      {/* Task Dialog */}
      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        defaultRelated={{
          type: "ACCOUNT",
          id: account.id,
          name: account.name,
        }}
        onSaved={() => {
          setTaskDialogOpen(false);
          fetch360();
        }}
      />
    </>
  );
}
